import { Capacitor } from '@capacitor/core';
import { registerWebPush, maintainWebPushRegistration, unregisterWebPush, getWebPushPermission, type PushAttemptResult, type PushMaintenanceResult } from './pushWeb';
import { registerNativePush, maintainNativePushRegistration, unregisterNativePush, getNativePushPermission } from './pushNative';
import { getLivePushIdentityKey, updatePushPreferenceAs, type GuardedPreferenceResult } from './pushSubscriptionWriter';

export type { PushAttemptResult, PushMaintenanceResult, GuardedPreferenceResult };
export type PushPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

/** An identity-scoped push operation: the immutable account key it was
 *  started for plus its irreversible epoch guard. */
export interface PushOpHandle {
  identityKey: string;
  stillValid: () => boolean;
}

/**
 * Push orchestration (Phase 11 — explicit opt-in). One contract for web and
 * native:
 *   • getPushPermissionState()    — READ-ONLY, never prompts.
 *   • requestPushPermission()     — the ONLY prompting path; call it strictly
 *     from an explicit user gesture (the Settings toggle). Nothing may call
 *     it on mount/login/reload/route/language change.
 *   • maintainPushRegistration()  — silent startup maintenance for an
 *     already-granted, already-opted-in device; structurally unable to
 *     prompt (its implementations never call a permission-request API).
 *   • beginPushOperation() / setPushEnabledPreference() /
 *     unregisterPushDevice() — identity-scoped account mutations; every
 *     network write they cause goes through the guarded seam
 *     (pushSubscriptionWriter), which re-verifies the live identity in the
 *     same synchronous turn as each dispatch.
 *
 * Identity safety: every operation captures the identity key it was started
 * for plus an irreversible operation epoch. Logout, account switching, and
 * A→B→A all leave old operations permanently stale.
 */
export async function getPushPermissionState(): Promise<PushPermissionState> {
  return Capacitor.isNativePlatform() ? getNativePushPermission() : getWebPushPermission();
}

// ── Operation coordination (module-level, shared by the AppShell startup
// hook and the Settings enable/disable paths) ───────────────────────────────
let maintainedKey: string | null = null;
let inFlight: Promise<PushMaintenanceResult> | null = null;
// Monotonic operation epoch: bumped whenever the operating identity CHANGES
// (a different key begins an operation, or logout invalidates). A superseded
// operation's guard can never turn true again — A→B→A leaves A's original
// operation stale forever, even though a fresh A operation is welcome.
let opEpoch = 0;
let lastOpKey: string | null = null;

function beginOperation(identityKey: string): { stillValid: () => boolean } {
  if (lastOpKey !== identityKey) {
    opEpoch++;
    lastOpKey = identityKey;
  }
  const myEpoch = opEpoch;
  return {
    stillValid: () => opEpoch === myEpoch && getLivePushIdentityKey() === identityKey,
  };
}

/**
 * Begin an identity-scoped push operation for the CURRENTLY authenticated
 * account (null when logged out). The Settings disable path uses this at
 * gesture time; the enable path gets its handle from requestPushPermission.
 */
export function beginPushOperation(): PushOpHandle | null {
  const identityKey = getLivePushIdentityKey();
  if (!identityKey) return null;
  const op = beginOperation(identityKey);
  return { identityKey, stillValid: op.stillValid };
}

/**
 * Idempotently ensure this already-granted device's token is registered for
 * the CURRENT authenticated identity. Never prompts, never touches
 * push_enabled. `identityKey` is the AuthContext identity anchor: repeat
 * calls for the same identity join the in-flight run or no-op after success;
 * a call for a DIFFERENT identity supersedes the old run, whose operation
 * guard then blocks any late backend write.
 */
export function maintainPushRegistration(identityKey: string): Promise<PushMaintenanceResult> {
  if (maintainedKey === identityKey) {
    return Promise.resolve({ status: 'skipped', reason: 'already-maintained' });
  }
  if (inFlight && lastOpKey === identityKey) return inFlight;

  const op = beginOperation(identityKey);
  const run = (Capacitor.isNativePlatform()
    ? maintainNativePushRegistration(identityKey, op.stillValid)
    : maintainWebPushRegistration(identityKey, op.stillValid)
  )
    .then((result): PushMaintenanceResult => {
      if (result.status === 'registered' && op.stillValid()) maintainedKey = identityKey;
      return result;
    })
    .catch((): PushMaintenanceResult => ({ status: 'failed' }))
    .finally(() => {
      if (inFlight === run) inFlight = null;
    });
  inFlight = run;
  return run;
}

/**
 * Drop all operation state (logout). Every in-flight operation's guard turns
 * false permanently; the next login's identity starts a fresh epoch.
 */
export function invalidatePushMaintenance(): void {
  maintainedKey = null;
  lastOpKey = null;
  opEpoch++;
}

export async function requestPushPermission(): Promise<PushAttemptResult> {
  // Coordinate with startup maintenance: never run the enable pipeline
  // concurrently with an in-flight maintenance registration.
  if (inFlight) await inFlight.catch(() => {});

  // The explicit enable is an identity-scoped operation too: capture who
  // clicked the toggle, so a registration that settles after a logout or an
  // account switch is silently discarded ('stale') instead of writing — or
  // erroring — under the newly active account.
  const identityKey = getLivePushIdentityKey();
  if (!identityKey) return { ok: false, reason: 'failed' };
  const op = beginOperation(identityKey);
  const handle: PushOpHandle = { identityKey, stillValid: op.stillValid };

  const result = Capacitor.isNativePlatform()
    ? await registerNativePush(identityKey, op.stillValid)
    : await registerWebPush(identityKey, op.stillValid);
  // Hand the caller the operation handle so follow-up steps (the preference
  // write, success/error presentation) stay bound to the same identity.
  return result.ok ? { ok: true, op: handle } : result;
}

/**
 * Persist push_enabled for the account that initiated `op` — through the
 * identity-guarded seam, so a write whose account switched away mid-flight
 * dispatches nothing and returns 'stale'.
 */
export async function setPushEnabledPreference(enabled: boolean, op: PushOpHandle): Promise<GuardedPreferenceResult> {
  return updatePushPreferenceAs(op.identityKey, enabled, op.stillValid);
}

/**
 * This device's disable/rollback cleanup — never prompts. Identity-bound:
 * the backend DELETE dispatches only while the initiating account is still
 * active (an A-initiated delayed DELETE under B's credentials would delete
 * B's subscription). A stale cleanup is a silent no-op reported as success —
 * the initiating account's push_enabled=false already suppresses delivery.
 */
export async function unregisterPushDevice(op?: PushOpHandle): Promise<boolean> {
  const handle = op ?? beginPushOperation();
  if (!handle) return true; // logged out: no account-scoped cleanup to do
  return Capacitor.isNativePlatform()
    ? unregisterNativePush(handle.identityKey, handle.stillValid)
    : unregisterWebPush(handle.identityKey, handle.stillValid);
}
